import get from 'lodash/get';
import moment from 'moment';

import {
  DataReference,
  DateFilter,
  PostQueryOperations,
} from '../../../@types';
import {
  parseDataValue,
  getRangeMatcher,
  formatValue,
} from '../../../utils/dataUtils';
import { getDateOptions, formatDateKey } from '../../../utils/momentUtils';
import { filterData } from './filter';

//
// Reference Map parser
//

export interface ReferenceMap {
  [key: string]: Array<{
    id: string;
    label: string;
    match: (value: string | number) => boolean;
  }>;
}

type ReferenceMapCreator = (input: {
  data: Record<string, unknown>;
  references?: DataReference;
  dateFilter: DateFilter;
  postQuery?: PostQueryOperations;
}) => ReferenceMap;

/**
 * Creates the reference map to be used by aggregations and data build
 * @param data
 * @param references
 * @param dateFilter
 * @returns
 */
export const createReferenceMap: ReferenceMapCreator = ({
  data,
  references,
  dateFilter,
  postQuery,
}) => {
  // Skip if references not defined
  if (!references) return {};

  const result: ReferenceMap = {};
  Object.entries(references).forEach(([id, ref]) => {
    // If the reference is dynamic, fetch it from the data object
    if (ref.type === 'dynamic') {
      result[id] = filterData(
        get(data, ref.rootKey, []) as Record<string, string | number>[],
        undefined,
        postQuery,
      ).map((d) => {
        const value = parseDataValue(d, ref.key, ref.dataType || 'string');
        const pLabel = ref.label
          ? formatValue(
              parseDataValue(d, ref.label, ref.dataType || 'string'),
              undefined,
              ref.mapping,
            )
          : formatValue(value, undefined, ref.mapping);

        return {
          id: `${value}`,
          label: `${pLabel}`, // should always be a string
          match: (v) => v === value,
        };
      });
    }
    // If the reference is static, parse the values array
    else if (ref.type === 'static') {
      result[id] = ref.values.map((value) => {
        if (value.range) {
          return {
            id: `${value.range}`,
            label: `${value.label}`,
            match: getRangeMatcher(value.range, value.rangeType || 'integer'),
          };
        }

        if (
          value.value &&
          Array.isArray(value.value) &&
          value.value.length > 0
        ) {
          const incomingValue = value.value;

          return {
            id: `${incomingValue}`,
            label: `${value.label}`,
            match: (v) =>
              typeof v === 'string'
                ? (incomingValue as string[]).includes(v)
                : (incomingValue as number[]).includes(v),
          };
        }

        return {
          id: `${value.value}`,
          label: `${value.label}`,
          match: (v) => v === value.value,
        };
      });
    }
    // If the reference is for a date filter
    else if (ref.type === 'date-filter') {
      result[id] = getDateOptions(
        dateFilter,
        ref.order || 'asc',
        ref.upToCurrentDate,
      ).map((d) => ({
        id: d.id,
        label: d.display,
        match: (v) => formatDateKey(dateFilter, v as string) === d.id,
      }));
    }
    // If the reference is for a truncated dates
    else if (ref.type === 'truncated-date') {
      // Make an entry for each day of the week
      if (ref.mode === 'day-of-week') {
        result[id] = [6, ...Array(6).keys()].map((idx) => {
          // Moment's isoWeekday starts at 1!
          const textValue = moment()
            .isoWeekday(idx + 1)
            .format('ddd');
          return {
            id: textValue,
            label: textValue,
            // Moment's isoWeekday starts at 1!
            match: (v) => Number(v) === idx + 1,
          };
        });
      }
      // Make an entry for each hour
      else if (ref.mode === 'hour') {
        result[id] = [...Array(24).keys()].map((idx) => {
          const textValue = `${idx >= 10 ? '' : '0'}${idx}h`;
          return {
            id: textValue,
            label: textValue,
            match: (v) => moment(v).hour() === idx,
          };
        });
      }
    }
  });

  return result;
};
